"use client";

import { useCallback, useMemo, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { BooksMenu } from "@/components/odds-screen/live/books-menu";
import { normalizeWhaleWatchBookKey } from "@/lib/whale-watch/whale-watch-shared";
import { useBookAvailability } from "@/contexts/book-availability-context";

export interface WhaleWatchBookFilterProps {
  availableBookKeys: string[];
  selectedBookKeys: string[];
  onToggleBookKey: (bookKey: string) => void;
  onClearAll: () => void;
  onSetBooks: (bookKeys: string[]) => void;
  savedBookKeys?: readonly string[];
  onUseSavedBooks?: () => void;
  usesGlobalPreferences?: boolean;
  isUsingGlobalDefaults?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const NOT_SAVED = { available: false, active: false, count: 0 } as const;

export function WhaleWatchBookFilter({
  availableBookKeys,
  selectedBookKeys,
  onToggleBookKey,
  onClearAll,
  onSetBooks,
  savedBookKeys,
  onUseSavedBooks,
  usesGlobalPreferences = false,
  isUsingGlobalDefaults = false,
  open,
  onOpenChange,
}: WhaleWatchBookFilterProps) {
  const isMobile = useIsMobile() === true;
  const { isHere } = useBookAvailability();
  const books = useMemo(() => {
    const seen = new Set<string>();
    for (const key of [...availableBookKeys, ...selectedBookKeys]) {
      const book = normalizeWhaleWatchBookKey(key);
      if (book) seen.add(book);
    }
    return [...seen];
  }, [availableBookKeys, selectedBookKeys]);
  const [pickedNone, setPickedNone] = useState(false);
  const none = pickedNone && open !== false;
  const selected = useMemo(() => new Set(selectedBookKeys.map(normalizeWhaleWatchBookKey)), [selectedBookKeys]);
  const showingAll = usesGlobalPreferences ? isUsingGlobalDefaults : selectedBookKeys.length === 0;
  const rawOn = useCallback(
    (book: string) => !none && (showingAll || selected.has(book)),
    [none, selected, showingAll],
  );
  const isBookEnabled = useCallback((book: string) => rawOn(book) && isHere(book), [isHere, rawOn]);
  const selectedCount = books.filter(isBookEnabled).length;

  const apply = useCallback((next: readonly string[]) => {
    setPickedNone(next.length === 0);
    if (next.length === 0) return;
    if (next.length === books.length && books.every((book) => next.includes(book))) onClearAll();
    else onSetBooks([...next]);
  }, [books, onClearAll, onSetBooks]);
  const onToggle = useCallback((book: string) => {
    if (!none && !showingAll) {
      if (selected.size === 1 && selected.has(book)) apply([]);
      else onToggleBookKey(book);
      return;
    }
    const current = books.filter(rawOn);
    apply(current.includes(book) ? current.filter((id) => id !== book) : [...current, book]);
  }, [apply, books, rawOn, none, onToggleBookKey, selected, showingAll]);
  const onSetMany = useCallback((ids: readonly string[], on: boolean) => {
    const next = new Set(books.filter(rawOn));
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    apply([...next]);
  }, [apply, books, rawOn]);
  const showAll = useCallback(() => { setPickedNone(false); onClearAll(); }, [onClearAll]);
  const saved = useMemo(() => {
    if (!savedBookKeys?.length || !onUseSavedBooks) return NOT_SAVED;
    const savedSet = new Set(savedBookKeys.map(normalizeWhaleWatchBookKey));
    if (books.every((book) => savedSet.has(book))) return NOT_SAVED;
    const active = books.every((book) => !isHere(book) || rawOn(book) === savedSet.has(book));
    return { available: true, active, count: savedSet.size };
  }, [books, isHere, onUseSavedBooks, rawOn, savedBookKeys]);
  const applySaved = useCallback(() => { setPickedNone(false); onUseSavedBooks?.(); }, [onUseSavedBooks]);

  return (
    <BooksMenu
      books={books}
      isBookEnabled={isBookEnabled}
      selectedCount={selectedCount}
      saved={saved}
      onUseSaved={saved.available ? applySaved : showAll}
      onShowAll={showAll}
      onNone={() => apply([])}
      onToggle={onToggle}
      onOnly={(book) => apply([book])}
      onSetMany={onSetMany}
      variant={isMobile ? "icon" : "labeled"}
      align="left"
      open={open}
      onOpenChange={(next) => {
        if (!next) setPickedNone(false);
        onOpenChange?.(next);
      }}
    />
  );
}
